import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AccountLegalNotice } from "../components/AccountLegalNotice";
import { HCaptchaField } from "../components/HCaptchaField";
import { useCustomer } from "../customer/CustomerContext";
import {
  emailMessage,
  isValidEmail,
  requiredMessage,
} from "../utils/formValidation";

export function RegisterPage() {
  const { customer, register } = useCustomer();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (customer) return <Navigate to="/account" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.first_name = requiredMessage("First name");
    if (!lastName.trim()) errs.last_name = requiredMessage("Last name");
    if (!email.trim()) errs.email = requiredMessage("Email");
    else if (!isValidEmail(email)) errs.email = emailMessage();
    if (!password) errs.password = requiredMessage("Password");
    else if (password.length < 8) errs.password = "Password must be at least 8 characters.";
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError("Please fill in the required fields.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await register({
        email: email.trim(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        h_captcha_response: captchaToken || undefined,
      });
      navigate("/account");
    } catch (err) {
      const fe = (err as { field_errors?: Record<string, string> }).field_errors;
      if (fe) setFieldErrors(fe);
      setError(err instanceof Error ? err.message : "Registration failed.");
      setCaptchaToken("");
      setCaptchaReset((n) => n + 1);
    } finally {
      setLoading(false);
    }
  }

  return (
    <article className="page account-page">
      <p className="eyebrow">Customer account</p>
      <h1>Register</h1>
      <p className="page-summary">
        Create an account to track purchases, save favorites, and manage your preferences.
      </p>
      <form className="deposit-form account-form" onSubmit={onSubmit} noValidate>
        <div className="buy-server-row">
          <label htmlFor="reg-first">
            First name <span className="req-star">*</span>
          </label>
          <div className="buy-server-control">
            <input
              id="reg-first"
              type="text"
              autoComplete="given-name"
              value={firstName}
              aria-invalid={Boolean(fieldErrors.first_name)}
              onChange={(e) => setFirstName(e.target.value)}
            />
            {fieldErrors.first_name ? (
              <p className="field-error">{fieldErrors.first_name}</p>
            ) : null}
          </div>
        </div>
        <div className="buy-server-row">
          <label htmlFor="reg-last">
            Last name <span className="req-star">*</span>
          </label>
          <div className="buy-server-control">
            <input
              id="reg-last"
              type="text"
              autoComplete="family-name"
              value={lastName}
              aria-invalid={Boolean(fieldErrors.last_name)}
              onChange={(e) => setLastName(e.target.value)}
            />
            {fieldErrors.last_name ? <p className="field-error">{fieldErrors.last_name}</p> : null}
          </div>
        </div>
        <div className="buy-server-row">
          <label htmlFor="reg-email">
            Email <span className="req-star">*</span>
          </label>
          <div className="buy-server-control">
            <input
              id="reg-email"
              type="email"
              autoComplete="email"
              value={email}
              aria-invalid={Boolean(fieldErrors.email)}
              onChange={(e) => setEmail(e.target.value)}
            />
            {fieldErrors.email ? <p className="field-error">{fieldErrors.email}</p> : null}
          </div>
        </div>
        <div className="buy-server-row">
          <label htmlFor="reg-password">
            Password <span className="req-star">*</span>
          </label>
          <div className="buy-server-control">
            <input
              id="reg-password"
              type="password"
              autoComplete="new-password"
              value={password}
              aria-invalid={Boolean(fieldErrors.password)}
              onChange={(e) => setPassword(e.target.value)}
            />
            {fieldErrors.password ? <p className="field-error">{fieldErrors.password}</p> : null}
          </div>
        </div>
        <HCaptchaField purpose="login" onToken={setCaptchaToken} resetKey={captchaReset} />
        {fieldErrors.captcha ? <p className="field-error">{fieldErrors.captcha}</p> : null}
        {error ? (
          <p className="model-run-error" role="alert">
            {error}
          </p>
        ) : null}
        <AccountLegalNotice mode="signup" />
        <div className="buy-server-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Creating account…" : "Create account"}
          </button>
        </div>
      </form>
      <p className="account-switch">
        Already have an account? <Link to="/account/login">Log in</Link>
      </p>
    </article>
  );
}
